/**
 * UI language (web / desktop use i18next, mobile uses easy_localization; translation files are all in locales/ at the repo root, see locales/README.md).
 *
 * Write Chinese directly in the source, wrapped as t('<Chinese text>', { vars }); the translation file key is that Chinese sentence, variables written as {name}.
 * Chinese is the source language and needs no translation file; other languages fall back to English for missing sentences, and to Chinese if English is missing too.
 * Messages from the backend (errors, sync status, notifications) are also Chinese: translated if locales/<language>/server.json has the same sentence;
 * ones with variables are matched as whole sentences with {name} as a wildcard (MessageMatcher).
 * Only pure functions shared by all three clients live here.
 */
import type { Language, LanguagePref } from './index.js';

export type Dict = Readonly<Record<string, string>>;
export type Vars = Readonly<Record<string, string | number>>;

/** Source language: the key itself is the text. */
export const SOURCE_LANGUAGE: Language = 'zh';

/** Replace {name} with the variable's value; leave ones not given as is. */
export function fill(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/**
 * Language chosen in settings → language actually used (must have a translation file).
 * Follow system: find the first available one in the system's language preference order (match the full code like zh-TW first, then the primary language zh); fall back to English if none.
 */
export function resolveLanguage(pref: LanguagePref | undefined, systemLocales: readonly string[], available: readonly Language[]): Language {
  const has = (code: string) => available.find((a) => a.toLowerCase() === code.toLowerCase());
  if (pref && pref !== 'system') {
    const hit = has(pref) ?? has(pref.split('-')[0]!);
    if (hit) return hit;
  }
  for (const locale of systemLocales) {
    const code = locale.replace('_', '-');
    const hit = has(code) ?? has(code.split('-')[0]!);
    if (hit) return hit;
  }
  return has('en') ?? SOURCE_LANGUAGE;
}

/** Fallback order when a language is missing a sentence: English → Chinese (the key itself). */
export function fallbackLanguages(lang: Language): Language[] {
  return lang === SOURCE_LANGUAGE || lang === 'en' ? [] : ['en'];
}

/** Language codes allowed in settings (like zh, en, ja, pt-BR). */
export function isLanguageCode(x: unknown): x is Language {
  return typeof x === 'string' && /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})?$/.test(x);
}

interface Pattern {
  re: RegExp;
  names: string[];
  value: string;
}

/** Whole-sentence message from the backend → current language (server.json). Translate if the sentence is in the dictionary or matches a sentence with variables; otherwise leave as is. */
export class MessageMatcher {
  private readonly patterns: Pattern[] = [];

  constructor(private readonly dict: Dict) {
    for (const [key, value] of Object.entries(dict)) {
      if (!/\{\w+\}/.test(key)) continue;
      const names: string[] = [];
      const source = key
        .split(/(\{\w+\})/)
        .map((part) => {
          const m = /^\{(\w+)\}$/.exec(part);
          if (!m) return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          names.push(m[1]!);
          return '([\\s\\S]*?)';
        })
        .join('');
      this.patterns.push({ re: new RegExp(`^${source}$`), names, value });
    }
  }

  translate(text: string): string {
    if (!text) return text;
    const exact = this.dict[text];
    if (exact !== undefined) return exact;
    for (const p of this.patterns) {
      const m = p.re.exec(text);
      if (m) return fill(p.value, Object.fromEntries(p.names.map((n, i) => [n, m[i + 1] ?? ''])));
    }
    return text;
  }
}
